import React, { useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';

import {
  AccountBalanceWallet,
  Warning
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../hooks/useAuth';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

interface WithdrawalFormData {
  amount: number;
  reason: string;
}

interface WithdrawalRequest {
  id: number;
  userId: number;
  amount: number;
  reason?: string;
  status: 'Pending' | 'Approved' | 'Rejected' | 'Completed';
  requestedAt: string;
  processedAt?: string;
  rejectionReason?: string;
}

const withdrawalSchema = yup.object().shape({
  amount: yup.number()
    .min(0.01, 'Amount must be greater than 0')
    .required('Amount is required'),
  reason: yup.string().default('')
});

const Withdrawal: React.FC = () => {
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [withdrawalData, setWithdrawalData] = useState<WithdrawalFormData | null>(null);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { control, handleSubmit, formState: { errors }, watch, reset } = useForm<WithdrawalFormData>({
    resolver: yupResolver(withdrawalSchema),
    defaultValues: {
      amount: 0,
      reason: ''
    }
  });

  const watchedAmount = watch('amount');

  // Fetch wallet balance
  const { data: walletData } = useQuery({
    queryKey: ['wallet', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const response = await axios.get(`/wallet/user/${user.id}`);
      return response.data;
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const walletBalance = walletData?.balance || 0;
  const canWithdraw = walletBalance >= watchedAmount && watchedAmount > 0;

  // Fetch withdrawal history
  const { data: withdrawalHistory } = useQuery<WithdrawalRequest[]>({
    queryKey: ['withdrawal-history'],
    queryFn: async () => {
      const response = await axios.get('/withdrawal');
      return response.data.data || response.data; // Handle paginated response
    }
  });

  const withdrawalMutation = useMutation({
    mutationFn: async (data: WithdrawalFormData) => {
      const response = await axios.post('/withdrawal/simple', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['withdrawal-history'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-stats'] });
      queryClient.invalidateQueries({ queryKey: ['wallet', user?.id] });
      reset();
      setConfirmDialog(false);
    }
  });

  const handleWithdrawalSubmit = (data: WithdrawalFormData) => {
    setWithdrawalData(data);
    setConfirmDialog(true);
  };

  const handleConfirmWithdrawal = () => {
    if (withdrawalData) {
      withdrawalMutation.mutate(withdrawalData);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed': return 'success';
      case 'approved': return 'info';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Withdrawal
      </Typography>

      <Grid container spacing={3}>
        {/* Withdrawal Form */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Request Withdrawal
            </Typography>

            <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AccountBalanceWallet sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="body2">Available Credits</Typography>
                    <Typography variant="h4">
                      ${walletBalance.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Box component="form" onSubmit={handleSubmit(handleWithdrawalSubmit)}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <Controller
                    name="amount"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Withdrawal Amount ($)"
                        type="number"
                        inputProps={{ min: 0.01, max: user?.credits || 0, step: 0.01 }}
                        error={!!errors.amount}
                        helperText={errors.amount?.message || `Maximum available: $${user?.credits?.toFixed(2) || '0.00'}`}
                      />
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="reason"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Reason (Optional)"
                        multiline
                        rows={3}
                        placeholder="Please provide a reason for this withdrawal"
                      />
                    )}
                  />
                </Grid>

                {!canWithdraw && watchedAmount > 0 && (
                  <Grid size={12}>
                    <Alert severity="error" icon={<Warning />}>
                      {watchedAmount > (user?.credits || 0) 
                        ? 'Insufficient credits for this withdrawal amount'
                        : 'Amount must be greater than 0'
                      }
                    </Alert>
                  </Grid>
                )}

                <Grid size={12}>
                  <Alert severity="info">
                    Withdrawal requests require admin approval and are processed within 1-3 business days.
                  </Alert>
                </Grid>

                <Grid size={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    disabled={!canWithdraw || withdrawalMutation.isPending}
                    size="large"
                  >
                    {withdrawalMutation.isPending ? 'Processing...' : 'Request Withdrawal'}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>

        {/* Withdrawal History */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Withdrawal History
            </Typography>

            {withdrawalHistory && withdrawalHistory.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {withdrawalHistory.map((withdrawal) => (
                      <TableRow key={withdrawal.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(withdrawal.requestedAt)}
                          </Typography>
                          {withdrawal.reason && (
                          <Typography variant="caption" color="text.secondary">
                              {withdrawal.reason}
                          </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            ${withdrawal.amount.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={withdrawal.status}
                            color={getStatusColor(withdrawal.status) as any}
                            size="small"
                            sx={{ textTransform: 'capitalize' }}
                          />
                          {withdrawal.status === 'Rejected' && withdrawal.rejectionReason && (
                            <Typography variant="caption" color="error" display="block">
                              {withdrawal.rejectionReason}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <AccountBalanceWallet sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                <Typography variant="body1" color="text.secondary">
                  No withdrawal history found
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog} onClose={() => setConfirmDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="warning" />
            Confirm Withdrawal Request
          </Box>
        </DialogTitle>
        <DialogContent>
          {withdrawalData && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Please review your withdrawal details:
              </Typography>
              
              <Card variant="outlined" sx={{ mt: 2 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Typography variant="body2" color="text.secondary">Amount</Typography>
                      <Typography variant="h6">${withdrawalData.amount.toFixed(2)}</Typography>
                    </Grid>
                    {withdrawalData.reason && (
                      <Grid size={12}>
                        <Typography variant="body2" color="text.secondary">Reason</Typography>
                        <Typography variant="body1">{withdrawalData.reason}</Typography>
                      </Grid>
                    )}
                  </Grid>
                </CardContent>
              </Card>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                Once submitted, this withdrawal request cannot be cancelled. 
                Please ensure all details are correct.
              </Alert>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleConfirmWithdrawal} 
            variant="contained" 
            disabled={withdrawalMutation.isPending}
          >
            {withdrawalMutation.isPending ? 'Submitting...' : 'Confirm Withdrawal'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Withdrawal;